import _find from 'lodash/find';
import _head from 'lodash/head';
import _uniqBy from 'lodash/uniqBy';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import moment from 'moment-timezone';
import { GraphQLFormattedError } from 'graphql';
import { helpers } from '@minecraft.utils';
import { ACC_CLAIM_LIMIT, MULTIPLE_TIME_QUALIFIED_ROLES } from 'constants/constants';
import { gqlQuery, gqlSystemRole } from 'graphqls/graphql';

import { getOnboardingToken, parseJwt } from '@blocs.auth';

type isConsecutiveUserWithQualifiedRoleProps = {
  roles: gqlSystemRole[];
  legacyClaim: number;
};

export const generateSelectOptions = (values: any): Array<any> =>
  values && !!values.length
    ? _map(values, item => ({ value: item.id, label: item.name, additionalValue: item.supportingCountryId }))
    : [];

export const findTimeZone = (timeZones: Array<any>): string | number => {
  const userTime = moment.tz
    .guess()
    .toUpperCase()
    .replace('/', '_');

  // here we need to check both - code and browserCode from DB, as firefox is giving different result
  const userTimeZone =
    timeZones &&
    !!timeZones.length &&
    _find(timeZones, time => userTime === time.codeBrowser || userTime === time.code);
  // if no value was found -> set first from timeZones
  return userTimeZone ? userTimeZone.id : _head(timeZones).id;
};

interface ErrorType extends GraphQLFormattedError {
  data: {
    errorCode: any;
  };
}

// gets error messages from API by code
export const getErrorMessage = (error): string => {
  const errorData = error?.graphQLErrors?.length && _head(error.graphQLErrors as Array<ErrorType>);
  if (!errorData) return 'Something went wrong';
  if (errorData.message) return errorData.message;
  return MESSAGES[ERROR[errorData?.data?.errorCode]] || 'Something went wrong';
};

export const getDmas = (dmas: gqlQuery['dmas']): any[] => {
  const countries = _uniqBy(
    _map(dmas, (dma: any) => dma.region.country),
    'id'
  );
  const regions = _uniqBy(
    _map(dmas, (dma: any) => dma.region),
    'id'
  );

  return _map(countries, (country: any) => ({
    id: country.id,
    code: country.code,
    name: country.name,
    regions: _map(
      _filter(regions, (region: any) => region.country.id === country.id),
      (region: any) => ({
        id: region.id,
        name: region.name,
        code: region.code,
        dmas: _map(
          _filter(dmas, (dma: any) => dma.region.id === region.id),
          (dma: any) => ({
            id: dma.id,
            name: dma.name,
            code: dma.code,
          })
        ),
      })
    ),
  }));
};

// TODO: Irina ZHavoronkova 20.12.19 - replace to minecraft
type SelectComplexItem = {
  value: string;
  label: string;
  options?: Array<any>;
};

export const sortLocations = (groupedLocations: Array<SelectComplexItem>): Array<SelectComplexItem> => {
  groupedLocations.forEach(country =>
    country.options
      .sort(helpers.sortLocationsByName)
      .forEach(region => region.options.sort(helpers.sortLocationsByName))
  );

  return groupedLocations;
};

export const isConsecutiveUserWithQualifiedRole = ({
  legacyClaim,
  roles,
}: isConsecutiveUserWithQualifiedRoleProps): boolean => {
  if (!roles.length) return false;

  const isQualifiedRole = MULTIPLE_TIME_QUALIFIED_ROLES.includes(roles[0].code);
  const isQualifiedLegacyClaimsCount = legacyClaim > 0 && legacyClaim <= ACC_CLAIM_LIMIT;

  return isQualifiedRole && isQualifiedLegacyClaimsCount;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trimAll = <T extends Record<string, any>>(obj: T): T => {
  if (typeof obj === 'object') {
    return _mapValues(obj, o => {
      if (typeof o === 'string') {
        return o.trim();
      }
      return o;
    });
  }
  return obj;
};

const SHOW_SUCCESSFULLY_VERIFIED_EMAIL_MESSAGE = 'showSuccessfullyVerifiedEmailMessage';
export const getEmailVerifiedMessage = (): string => localStorage.getItem(SHOW_SUCCESSFULLY_VERIFIED_EMAIL_MESSAGE);
export const setEmailVerifiedMessage = (value): void =>
  localStorage.setItem(SHOW_SUCCESSFULLY_VERIFIED_EMAIL_MESSAGE, value);

export const getUserCountryInstance = (): string | null => {
  const token = getOnboardingToken();
  const parsedToken = parseJwt(token);
  const allowedInstances: Array<{ [key: string]: any }> = helpers.getAllowedInstances();
  return allowedInstances.find(instance => instance.value === parsedToken?.legacyInstance)?.item || null;
};
